/**
 * Routing Service - Fetches real road directions using OSRM (Open Source Routing Machine)
 * This is free and doesn't require an API key!
 */

export interface RouteCoordinate {
  latitude: number;
  longitude: number;
}

export interface RouteResult {
  coordinates: RouteCoordinate[];
  distance: number; // meters
  duration: number; // seconds
  success: boolean;
  error?: string;
}

export interface RouteStep {
  instruction: string;
  distance: number;
  duration: number;
  maneuver: {
    type: string;
    modifier?: string;
    bearing_before?: number;
    bearing_after?: number;
    location: [number, number];
  };
}

export interface DetailedRouteResult extends RouteResult {
  steps: RouteStep[];
}

// OSRM public server - free to use with reasonable limits
const OSRM_BASE_URL = 'https://router.project-osrm.org';

/**
 * Get route between two points using OSRM
 */
export async function getRoute(
  start: RouteCoordinate,
  end: RouteCoordinate,
  profile: 'driving' | 'walking' | 'cycling' = 'driving'
): Promise<RouteResult> {
  try {
    // OSRM expects coordinates as longitude,latitude
    const startCoord = `${start.longitude},${start.latitude}`;
    const endCoord = `${end.longitude},${end.latitude}`;
    
    const url = `${OSRM_BASE_URL}/route/v1/${profile}/${startCoord};${endCoord}?overview=full&geometries=geojson`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`OSRM API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
      return {
        coordinates: [start, end],
        distance: calculateStraightLineDistance(start, end),
        duration: 0,
        success: false,
        error: data.message || 'No route found',
      };
    }
    
    const route = data.routes[0];
    const geometry = route.geometry;
    
    // Convert GeoJSON coordinates [lng, lat] to our format { latitude, longitude }
    const coordinates: RouteCoordinate[] = geometry.coordinates.map(
      (coord: [number, number]) => ({
        longitude: coord[0],
        latitude: coord[1],
      })
    );
    
    return {
      coordinates,
      distance: route.distance,
      duration: route.duration,
      success: true,
    };
  } catch (error) {
    console.error('Routing error:', error);
    // Fallback to straight line
    return {
      coordinates: [start, end],
      distance: calculateStraightLineDistance(start, end),
      duration: 0,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get detailed route with turn-by-turn instructions
 */
export async function getDetailedRoute(
  start: RouteCoordinate,
  end: RouteCoordinate,
  profile: 'driving' | 'walking' | 'cycling' = 'driving'
): Promise<DetailedRouteResult> {
  try {
    const startCoord = `${start.longitude},${start.latitude}`;
    const endCoord = `${end.longitude},${end.latitude}`;
    
    const url = `${OSRM_BASE_URL}/route/v1/${profile}/${startCoord};${endCoord}?overview=full&geometries=geojson&steps=true`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`OSRM API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
      return {
        coordinates: [start, end],
        distance: calculateStraightLineDistance(start, end),
        duration: 0,
        success: false,
        error: data.message || 'No route found',
        steps: [],
      };
    }
    
    const route = data.routes[0];
    const geometry = route.geometry;
    
    const coordinates: RouteCoordinate[] = geometry.coordinates.map(
      (coord: [number, number]) => ({
        longitude: coord[0],
        latitude: coord[1],
      })
    );
    
    // Extract steps from legs
    const steps: RouteStep[] = [];
    for (const leg of route.legs) {
      for (const step of leg.steps) {
        steps.push({
          instruction: formatInstruction(step.maneuver),
          distance: step.distance,
          duration: step.duration,
          maneuver: {
            type: step.maneuver.type,
            modifier: step.maneuver.modifier,
            bearing_before: step.maneuver.bearing_before,
            bearing_after: step.maneuver.bearing_after,
            location: step.maneuver.location,
          },
        });
      }
    }
    
    return {
      coordinates,
      distance: route.distance,
      duration: route.duration,
      success: true,
      steps,
    };
  } catch (error) {
    console.error('Detailed routing error:', error);
    return {
      coordinates: [start, end],
      distance: calculateStraightLineDistance(start, end),
      duration: 0,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      steps: [],
    };
  }
}

/**
 * Get routes for multiple waypoints
 */
export async function getMultiPointRoute(
  waypoints: RouteCoordinate[],
  profile: 'driving' | 'walking' | 'cycling' = 'driving'
): Promise<RouteResult> {
  if (waypoints.length < 2) {
    return {
      coordinates: waypoints,
      distance: 0,
      duration: 0,
      success: false,
      error: 'Need at least 2 waypoints',
    };
  }
  
  try {
    const coordsString = waypoints
      .map(wp => `${wp.longitude},${wp.latitude}`)
      .join(';');
    
    const url = `${OSRM_BASE_URL}/route/v1/${profile}/${coordsString}?overview=full&geometries=geojson`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`OSRM API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
      return {
        coordinates: waypoints,
        distance: 0,
        duration: 0,
        success: false,
        error: data.message || 'No route found',
      };
    }
    
    const route = data.routes[0];
    const geometry = route.geometry;
    
    const coordinates: RouteCoordinate[] = geometry.coordinates.map(
      (coord: [number, number]) => ({
        longitude: coord[0],
        latitude: coord[1],
      })
    );
    
    return {
      coordinates,
      distance: route.distance,
      duration: route.duration,
      success: true,
    };
  } catch (error) {
    console.error('Multi-point routing error:', error);
    return {
      coordinates: waypoints,
      distance: 0,
      duration: 0,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Calculate straight-line distance between two points (Haversine formula)
 */
function calculateStraightLineDistance(
  start: RouteCoordinate,
  end: RouteCoordinate
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (start.latitude * Math.PI) / 180;
  const φ2 = (end.latitude * Math.PI) / 180;
  const Δφ = ((end.latitude - start.latitude) * Math.PI) / 180;
  const Δλ = ((end.longitude - start.longitude) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Format OSRM maneuver into human-readable instruction (Swedish)
 */
function formatInstruction(maneuver: { type: string; modifier?: string }): string {
  const { type, modifier } = maneuver;
  
  const modifierMap: Record<string, string> = {
    'sharp right': 'skarpt höger',
    'right': 'höger',
    'slight right': 'svagt höger',
    'straight': 'rakt fram',
    'slight left': 'svagt vänster',
    'left': 'vänster',
    'sharp left': 'skarpt vänster',
    'uturn': 'U-sväng',
  };
  
  const typeMap: Record<string, string> = {
    'turn': 'Sväng',
    'new name': 'Fortsätt på',
    'depart': 'Starta',
    'arrive': 'Ankomst',
    'merge': 'Kör in på',
    'ramp': 'Ta avfart',
    'on ramp': 'Ta påfart',
    'off ramp': 'Ta avfart',
    'fork': 'Vid delning, håll',
    'end of road': 'Vid vägens slut, sväng',
    'continue': 'Fortsätt',
    'roundabout': 'I rondellen, ta',
    'rotary': 'I rondellen, ta',
    'roundabout turn': 'I rondellen, sväng',
    'notification': '',
    'exit roundabout': 'Lämna rondellen',
    'exit rotary': 'Lämna rondellen',
  };
  
  const typeText = typeMap[type] || type;
  const modifierText = modifier ? modifierMap[modifier] || modifier : '';
  
  if (type === 'arrive') {
    return '🎯 Du har ankommit till destinationen';
  }
  
  if (type === 'depart') {
    return `🚀 Starta och kör ${modifierText}`;
  }
  
  if (modifierText) {
    return `${typeText} ${modifierText}`;
  }
  
  return typeText;
}

/**
 * Format distance for display
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
}

/**
 * Format duration for display
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)} sek`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} h ${remainingMinutes} min`;
}

// Cache for routes to avoid repeated API calls
const routeCache = new Map<string, { result: RouteResult; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get route with caching
 */
export async function getCachedRoute(
  start: RouteCoordinate,
  end: RouteCoordinate,
  profile: 'driving' | 'walking' | 'cycling' = 'driving'
): Promise<RouteResult> {
  const cacheKey = `${start.latitude},${start.longitude}-${end.latitude},${end.longitude}-${profile}`;
  
  const cached = routeCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.result;
  }
  
  const result = await getRoute(start, end, profile);
  routeCache.set(cacheKey, { result, timestamp: Date.now() });
  
  return result;
}

/**
 * Clear route cache
 */
export function clearRouteCache(): void {
  routeCache.clear();
}
